import type { ReactElement, ReactNode, Ref } from "react";
import React from "react";
import cn from "~/kernel/styling/className";
import isPresent from "~/kernel/validation/isPresent";

const PADDINGS = Object.freeze({
  none: "",
  small: "p-3",
  medium: "p-5",
  default: "px-3 py-8 md:p-12",
});

type Padding = keyof typeof PADDINGS;

type FlatCardProps = {
  children: ReactNode;
  active?: boolean;
  variant?: "default" | "muted";
  header?: ReactElement | null;
  footer?: ReactElement | null;
  padding?: Padding;
  onClick?: () => void;
};

const FlatCard = React.forwardRef<HTMLElement, FlatCardProps>(
  (
    {
      children,
      variant = "default",
      header = null,
      padding = "default",
      onClick,
      active,
      footer = null,
    },
    ref,
  ) => {
    const isClickable = isPresent(onClick);
    const wrapperClassName = cn("flex-1 border rounded-lg border-slate-300", {
      "hover:border-secondary block w-full": isClickable,
      "ring-secondary ring-2 border-transparent": active,
      "bg-white": variant === "default",
      "bg-slate-50": variant === "muted",
    });
    const baseBodyClassName = cn("flex-1 text-left", PADDINGS[padding]);

    if (isClickable) {
      return (
        <button
          type="button"
          ref={ref as Ref<HTMLButtonElement>}
          onClick={onClick}
          className={wrapperClassName}
        >
          {header}
          <div className={baseBodyClassName}>{children}</div>
          {footer}
        </button>
      );
    }

    return (
      <div ref={ref as Ref<HTMLDivElement>} className={wrapperClassName}>
        {header}
        <div className={baseBodyClassName}>{children}</div>
        {footer}
      </div>
    );
  },
);

FlatCard.displayName = "FlatCard";

export default FlatCard;

type FlatCardHeaderProps = {
  children: ReactNode;
  padding?: Padding;
};

export function FlatCardHeader({
  children,
  padding = "default",
}: FlatCardHeaderProps) {
  return (
    <div
      className={cn(
        "overflow-hidden border-b border-slate-300",
        PADDINGS[padding],
      )}
    >
      {children}
    </div>
  );
}

type FlatCardFooterProps = {
  children: ReactNode;
  padding?: Padding;
};

export function FlatCardFooter({
  children,
  padding = "default",
}: FlatCardFooterProps) {
  return (
    <div
      className={cn(
        "overflow-hidden border-t border-slate-300",
        PADDINGS[padding],
      )}
    >
      {children}
    </div>
  );
}
